import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { activeSubNavItemId, type ModuleSubNavItem } from "./moduleSubNavMatch";

export type { ModuleSubNavItem } from "./moduleSubNavMatch";

export interface ModuleSubNavProps {
  /** The module's name, for the landmark: "{module} sections". */
  module: string;
  /**
   * The module's L1 sections, in the order MODULE_MAPS.md gives: Overview, primary
   * collections, secondary collections, Settings last.
   */
  items: ModuleSubNavItem[];
  /**
   * Sections that have no place in the module's map yet, kept behind a "More" menu at the end
   * of the bar so they stay reachable instead of vanishing. Counted toward the "two or more
   * sections" rule; the menu trigger is underlined while one of them is the current page.
   */
  moreItems?: ModuleSubNavItem[];
  /**
   * The path that decides the current section, when it isn't the URL's: a record shared by
   * several sections (a company opened from Organizations) reports the section it was opened
   * from. Defaults to the location's pathname.
   */
  currentPath?: string;
  className?: string;
}

/**
 * The module sub-nav (NAVIGATION_AND_STATES.md § Module sub-nav, MODULE_MAPS.md): a 40px bar
 * under the topbar listing the module's L1 sections, the active one underlined 2px in the
 * module accent. It renders only when the module has two or more sections, persists from L0
 * through L2, and is not rendered by L3 tasks (T4/T6), which own the screen.
 *
 * Links keep the `?ui=` layout preview, which may be the only preview state when storage is
 * blocked. From 640px it's a bar (scrolling sideways below 1024px, with 44px targets); below
 * 640px it's a dropdown showing the current section, for the caller to place under the title
 * (SURFACES.md § Responsive).
 *
 * @example
 * <ModuleSubNav module="People" items={[
 *   { id: "everyone", label: "Everyone", href: "/people", match: ["/people/"] },
 *   { id: "life-events", label: "Life events", href: "/people/life-events" },
 * ]} />
 */
export function ModuleSubNav({ module, items, moreItems = [], currentPath, className }: ModuleSubNavProps) {
  const { pathname, search } = useLocation();
  // Phones get a dropdown under the title, not a scrolling bar (SURFACES.md § Responsive).
  const isPhone = useIsMobile(640);
  if (items.length + moreItems.length < 2) return null;

  // One longest-prefix match across both lists, so a "More" page never lights up a bar item
  // that only owns its parent path (and vice versa).
  const activeId = activeSubNavItemId([...items, ...moreItems], currentPath ?? pathname);
  const moreActive = moreItems.some((item) => item.id === activeId);
  const ui = new URLSearchParams(search).get("ui");
  const withPreview = (href: string) => {
    if (!ui) return href;
    const [path, query = ""] = href.split("?");
    const params = new URLSearchParams(query);
    params.set("ui", ui);
    return `${path}?${params.toString()}`;
  };

  if (isPhone) {
    const active = [...items, ...moreItems].find((item) => item.id === activeId);
    return (
      <nav aria-label={`${module} sections`} className={className}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="min-h-11 w-full justify-between"
              aria-label={`${module} section: ${active?.label ?? "choose a section"}`}
            >
              <span>{active?.label ?? module}</span>
              <ChevronDown className="size-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
            {items.map((item) => (
              <DropdownMenuItem key={item.id} asChild className="min-h-11">
                <Link to={withPreview(item.href)} aria-current={item.id === activeId ? "page" : undefined}>
                  {item.label}
                </Link>
              </DropdownMenuItem>
            ))}
            {moreItems.length > 0 ? (
              <>
                {items.length > 0 ? <DropdownMenuSeparator /> : null}
                {moreItems.map((item) => (
                  <DropdownMenuItem key={item.id} asChild className="min-h-11">
                    <Link to={withPreview(item.href)} aria-current={item.id === activeId ? "page" : undefined}>
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>
    );
  }

  const linkClass = (active: boolean) =>
    cn(
      // 44px targets below 1024px, the 40px bar above (ACCESSIBILITY.md § Target size).
      "-mb-px inline-flex min-h-11 items-center whitespace-nowrap border-b-2 px-3 text-[13px] font-semibold transition-colors lg:h-10 lg:min-h-0",
      // Inset: the bar scrolls sideways, and a scrollport clips anything drawn
      // outside the link, so an outward ring would lose its top and bottom.
      "focus-visible:outline-none focus-visible:ring-[var(--focus-ring-width)] focus-visible:ring-inset focus-visible:ring-ring",
      active
        ? "border-[var(--module-color,hsl(var(--primary)))] text-[var(--forge-text)]"
        : "border-transparent text-muted-foreground hover:text-[var(--forge-text)]",
    );

  return (
    <nav
      aria-label={`${module} sections`}
      className={cn("-mx-1 overflow-x-auto border-b border-border/60 px-1", className)}
    >
      <ul className="flex w-max items-stretch gap-1">
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <li key={item.id} className="flex">
              <Link
                to={withPreview(item.href)}
                aria-current={active ? "page" : undefined}
                className={linkClass(active)}
              >
                {item.label}
              </Link>
            </li>
          );
        })}
        {moreItems.length > 0 ? (
          <li className="flex">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(linkClass(moreActive), "gap-1")}
                  // Not aria-current: that's for a link to the current page, and the trigger
                  // isn't one. The current entry inside the menu carries it.
                  aria-label={moreActive ? "More sections (includes the current page)" : "More sections"}
                >
                  More
                  <ChevronDown className="size-3.5" aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {moreItems.map((item) => (
                  <DropdownMenuItem key={item.id} asChild className="min-h-11 lg:min-h-8">
                    <Link to={withPreview(item.href)} aria-current={item.id === activeId ? "page" : undefined}>
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </li>
        ) : null}
      </ul>
    </nav>
  );
}
